import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Sparkle, Lightning, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { CHANGELOG as STATIC_CHANGELOG, LATEST_VERSION as STATIC_LATEST } from '../data/changelog';
import { openTour } from './GuidedTour';
import { getChangelog } from '../lib/api';

const STORAGE_KEY = 'stockaris_whatsnew_seen';

export default function WhatsNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [releases, setReleases] = useState(STATIC_CHANGELOG);
  const [latestVersion, setLatestVersion] = useState(STATIC_LATEST);

  // Pull live releases from MongoDB (admin-editable). Fall back to static seed on error.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getChangelog()
      .then(({ data }) => {
        if (cancelled) return;
        if (data && Array.isArray(data.releases) && data.releases.length > 0) {
          setReleases(data.releases);
          setLatestVersion(data.latest_version || data.releases[0].version);
        }
      })
      .catch(() => { /* keep static fallback */ });
    return () => { cancelled = true; };
  }, [user]);

  const markSeen = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, latestVersion);
  }, [latestVersion]);

  const handleCta = useCallback((cta) => {
    if (!cta) return;
    setOpen(false);
    markSeen();
    // Wait for Radix Sheet close animation (~300ms) to finish before acting.
    // Avoids orphaned overlays when we unmount/navigate mid-animation.
    const ACTION_DELAY = 450;
    if (cta.type === 'tour') {
      localStorage.removeItem('stockaris_tour_seen_v1');
      setTimeout(() => openTour(), ACTION_DELAY);
    } else if (cta.type === 'navigate' && cta.to) {
      setTimeout(() => navigate(cta.to), ACTION_DELAY);
    } else if (cta.type === 'external' && cta.href) {
      window.open(cta.href, '_blank', 'noopener,noreferrer');
    }
  }, [markSeen, navigate]);

  // Auto-open once per version, after login, on desktop.
  // Defers while the guided tour is running (or has orphan DOM overlays)
  // so the two overlays don't stack on top of each other.
  useEffect(() => {
    if (!user) return;
    if (window.innerWidth < 768) return;
    if (!latestVersion) return;
    // iter229 — demo tenants never see the Stockaris What's New drawer;
    // judges are on their own guided challenge.
    if (user?.company?.is_demo) return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (seen === latestVersion) return;

    let cancelled = false;
    const tryOpen = () => {
      if (cancelled) return;
      const tourSeen = localStorage.getItem('stockaris_tour_seen_v1');
      if (!tourSeen) return; // tour hasn't run yet
      const tourActive = document.querySelector(
        '.introjs-overlay, .introjs-helperLayer, .introjs-tooltipReferenceLayer'
      );
      if (tourActive) {
        // Tour UI still present; retry shortly
        setTimeout(tryOpen, 800);
        return;
      }
      setOpen(true);
    };
    const t = setTimeout(tryOpen, 1500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [user, latestVersion]);

  // Listen for manual open from sidebar button
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('stockaris:open-whats-new', handler);
    return () => window.removeEventListener('stockaris:open-whats-new', handler);
  }, []);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) markSeen();
  };

  if (!user) return null;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto bg-card p-0 border-l-2 border-brand"
        data-testid="whats-new-drawer"
      >
        <SheetHeader className="p-6 pb-4 border-b border-border bg-gradient-to-br from-[#F8FAFC] to-white">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 bg-brand flex items-center justify-center">
              <Sparkle size={18} weight="fill" className="text-white" />
            </div>
            <span className="text-[10px] font-bold tracking-[0.2em] text-brand uppercase">
              Release Notes
            </span>
          </div>
          <SheetTitle className="text-2xl font-bold tracking-tight text-left">
            What&rsquo;s New in Stockaris
          </SheetTitle>
          <SheetDescription className="text-left text-sm text-muted-foreground">
            Your warehouse keeps getting sharper. Here&rsquo;s what shipped lately.
          </SheetDescription>
        </SheetHeader>

        <div className="p-6 space-y-7" data-testid="whats-new-list">
          {releases.map((release, idx) => (
            <article
              key={release.version}
              data-testid={`whats-new-release-${release.version}`}
              className="relative pl-6"
            >
              {/* Timeline dot + line */}
              <div
                className={`absolute left-0 top-1.5 w-2.5 h-2.5 ${
                  idx === 0 ? 'bg-brand' : 'bg-slate-300'
                }`}
              />
              {idx !== releases.length - 1 && (
                <div className="absolute left-[4px] top-5 bottom-[-28px] w-px bg-slate-200" />
              )}

              <div className="flex items-baseline justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold text-foreground">
                    v{release.version}
                  </span>
                  {idx === 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-brand text-white text-[9px] font-bold tracking-wider uppercase">
                      <Lightning size={9} weight="fill" />
                      Latest
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{release.date}</span>
              </div>
              <p className="text-[10px] font-bold tracking-wider uppercase text-brand mb-3">
                {release.tag}
              </p>

              <ul className="space-y-3">
                {release.items.map((item, i) => (
                  <li key={i} className="border-l-2 border-slate-100 pl-3">
                    <p className="text-sm font-bold text-foreground mb-0.5">
                      {item.title}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.body}
                    </p>
                    {item.cta && (
                      <button
                        type="button"
                        onClick={() => handleCta(item.cta)}
                        data-testid={`whats-new-cta-${release.version}-${i}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand hover:text-[#001F8A] hover:gap-2.5 transition-all group"
                      >
                        <span className="border-b-2 border-brand group-hover:border-[#001F8A] pb-0.5">
                          {item.cta.label}
                        </span>
                        <ArrowRight size={12} weight="bold" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </article>
          ))}

          <div className="pt-4 mt-4 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">
              Shipped by the Stockaris team · Press{' '}
              <kbd className="px-1.5 py-0.5 bg-muted border border-border font-mono text-[10px]">
                Esc
              </kbd>{' '}
              to close
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Helper for other components to trigger the drawer
export function openWhatsNew() {
  window.dispatchEvent(new CustomEvent('stockaris:open-whats-new'));
}
